import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Container, Row, Col, Card, Form, Button, Table, Modal, Alert, Spinner, Badge
} from 'react-bootstrap';
import {
  PlusLg, Trash, Upload, Files, ClockFill
} from 'react-bootstrap-icons';
import apiClient from './lib/apiClient';
import dayjs from 'dayjs';
import { localInputToApiUtc } from './lib/dateUtils';
import logoImage from './images/WazzaScore_Logo2.png';
import { useDevMode } from './context/DevModeContext';
import React from 'react';
import { useAuth } from './context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';

// Custom styles for dark theme
const styles = {
  page: {
    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #0a0a0a 100%)',
    minHeight: '100vh',
    paddingTop: '2rem',
    paddingBottom: '2rem'
  },
  card: {
    background: 'rgba(26, 26, 26, 0.95)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    backdropFilter: 'blur(10px)'
  },
  cardHeader: {
    background: 'rgba(0, 0, 0, 0.3)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    color: '#fff',
    fontWeight: 600
  },
  input: {
    background: '#fff',
    border: '1px solid #333',
    borderRadius: '8px',
    fontSize: '14px'
  },
  select: {
    background: '#fff',
    border: '1px solid #333',
    borderRadius: '8px',
    fontSize: '14px'
  },
  tableHeader: {
    background: '#0a0a0a',
    color: '#fff',
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  tableCell: {
    background: 'rgba(17, 17, 17, 0.9)',
    borderColor: 'rgba(51, 51, 51, 0.5)',
    verticalAlign: 'middle',
    padding: '4px 6px'
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #00b341 0%, #00d855 100%)',
    border: 'none',
    fontWeight: 600,
    borderRadius: '8px',
    padding: '10px 20px'
  },
  btnOutline: {
    borderColor: 'rgba(255, 255, 255, 0.3)',
    color: '#fff',
    borderRadius: '8px'
  }
};

// Constants for classifications - unchanged
const CLASSIFICATIONS = [
  '19A', '19B', '19C', '19D', '19E', '19F', '19G',
  '18A', '18B', '18C', '18D', '18E', '18F', '18G',
  '17A', '17B', '17C', '17D', '17E', '17F', '17G',
  '16A', '16B', '16C', '16D', '16E', '16F', '16G',
  '15A', '15B', '15C', '15D', '15E', '15F', '15G',
  '14A', '14B', '14C', '14D', '14E', '14F', '14G',
  '13A', '13B', '13C', '13D', '13E', '13F', '13G',
  '12A', '12B', '12C', '12D', '12E', '12F', '12G',
  '11A', '11B', '11C', '11D', '11E', '11F', '11G',
  '10A', '10B', '10C', '10D', '10E', '10F', '10G',
  '9A', '9B', '9C', '9D', '9E', '9F', '9G'
];

// Default fixture object - unchanged
const defaultFixture = {
  id: null,
  teamOneId: '',
  teamOneName: '',
  teamOneClassification: '',
  teamOneScore: '',
  teamTwoId: '',
  teamTwoName: '',
  teamTwoClassification: '',
  teamTwoScore: '',
  fixtureTime: '14:00',
  homeTeamId: 'Neutral',
  homeTeamName: '',
};

// Validation utilities - unchanged
const validateFixture = (fixture, getTeamDetails) => {
  const errors = {};
  if (!fixture.teamOneId?.trim()) errors.teamOne = 'Club 1 required';
  if (!fixture.teamTwoId?.trim()) errors.teamTwo = 'Club 2 required';
  if (fixture.teamOneId === fixture.teamTwoId && fixture.teamOneId) errors.match = 'Same club selected';
  if (!fixture.teamOneClassification?.trim()) {
    errors.classOne = 'Classification required';
  } else {
    getTeamDetails(fixture.teamOneId, fixture.teamOneClassification);
  }
  if (!fixture.teamTwoClassification?.trim()) {
    errors.classTwo = 'Classification required';
  } else {
    getTeamDetails(fixture.teamTwoId, fixture.teamTwoClassification);
  }
  const scoreOne = parseInt(fixture.teamOneScore, 10);
  const scoreTwo = parseInt(fixture.teamTwoScore, 10);
  if (isNaN(scoreOne) || scoreOne < 0) errors.scoreOne = 'Valid score required';
  if (isNaN(scoreTwo) || scoreTwo < 0) errors.scoreTwo = 'Valid score required';
  if (!fixture.fixtureTime?.trim()) errors.time = 'Time required';
  return errors;
};

// Fetch all teams from the .NET API. The /dataimport/teams endpoint returns
// {teamName, classification, possibleTeamNames, teamId} per team. There's no
// dedicated club identifier in that DTO, so we use the lowercased club name
// as a synthetic clubId — sufficient for the picker's grouping needs.
const fetchTeamsFromApi = async (apiDomain) => {
  const { data: teams } = await apiClient.get(`${apiDomain}/dataimport/teams`);
  if (!Array.isArray(teams) || teams.length === 0) {
    return { allTeams: [], uniqueClubs: [] };
  }

  const allTeams = teams
    .map((t) => ({
      teamid: t.teamId,
      clubname: t.teamName,
      teamclassification: t.classification,
      clubid: t.teamName,
    }))
    .sort(
      (a, b) =>
        a.clubname.localeCompare(b.clubname) ||
        a.teamclassification.localeCompare(b.teamclassification)
    );

  const uniqueClubMap = new Map();
  for (const team of allTeams) {
    const key = team.clubname.toLowerCase();
    if (!uniqueClubMap.has(key)) {
      uniqueClubMap.set(key, { clubId: team.clubname, name: team.clubname });
    }
  }
  const uniqueClubs = Array.from(uniqueClubMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return { allTeams, uniqueClubs };
};

// Searchable Select Component - Custom dropdown with Portal for proper positioning
const SearchableSelect = React.memo(({ options = [], value, onChange, placeholder, loading }) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false });

  // Get selected option display name
  const selectedOption = options?.find(opt => opt.clubId === value || opt.id === value);

  // Filter options based on input - no artificial limit, let scrolling handle it
  const filteredOptions = useMemo(() => {
    if (!inputValue || inputValue.length < 1) {
      return options; // Show all options
    }
    return options.filter(opt =>
      (opt.name || opt.clubname || '').toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [options, inputValue]);

  // When value changes externally, update input display
  useEffect(() => {
    if (selectedOption && !isOpen) {
      setInputValue(selectedOption.name || selectedOption.clubname || '');
    }
  }, [selectedOption, isOpen]);

  // Update dropdown position - recalculate on scroll/resize since we use fixed positioning
  const updatePosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const dropdownHeight = 250;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      setDropdownPosition({
        top: openUpward ? rect.top - dropdownHeight : rect.bottom + 2,
        left: rect.left,
        width: rect.width,
        openUpward
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, updatePosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && !inputRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        // Revert to selected value on blur
        if (selectedOption) {
          setInputValue(selectedOption.name || selectedOption.clubname || '');
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedOption]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions.length]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
  };

  const handleSelectOption = (option) => {
    onChange(option);
    setInputValue(option.name || option.clubname || '');
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        if (selectedOption) {
          setInputValue(selectedOption.name || selectedOption.clubname || '');
        }
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  if (loading) {
    return (
      <div className="form-control d-flex align-items-center" style={styles.input}>
        <Spinner size="sm" animation="border" variant="success" className="me-2" />
        <span style={{ color: '#888', fontSize: '14px' }}>Loading...</span>
      </div>
    );
  }

  const dropdownStyles = {
    position: 'fixed',
    top: dropdownPosition.top,
    left: dropdownPosition.left,
    width: dropdownPosition.width,
    maxHeight: '250px',
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #ccc',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 9999,
  };

  const optionStyles = (isHighlighted) => ({
    padding: '8px 12px',
    cursor: 'pointer',
    background: isHighlighted ? '#00b341' : '#fff',
    color: isHighlighted ? '#fff' : '#333',
    fontSize: '14px',
    borderBottom: '1px solid #eee',
  });

  return (
    <>
      <Form.Control
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        style={styles.input}
        autoComplete="off"
      />
      {isOpen && filteredOptions.length > 0 && createPortal(
        <div ref={dropdownRef} style={dropdownStyles}>
          {filteredOptions.map((option, idx) => (
            <div
              key={option.clubId || option.id || idx}
              style={optionStyles(idx === highlightedIndex)}
              onClick={() => handleSelectOption(option)}
              onMouseEnter={() => setHighlightedIndex(idx)}
            >
              {option.name || option.clubname}
            </div>
          ))}
        </div>,
        document.body
      )}
      {isOpen && filteredOptions.length === 0 && inputValue && createPortal(
        <div ref={dropdownRef} style={dropdownStyles}>
          <div style={{ padding: '12px', color: '#888', textAlign: 'center' }}>
            No matches found
          </div>
        </div>,
        document.body
      )}
    </>
  );
});

// Sub-component: Fixture Settings
const FixtureSettings = React.memo(({ sharedDate, setSharedDate, uniqueClubs, teamsLoading, defaultTeamOne, setDefaultTeamOne, defaultTeamTwo, setDefaultTeamTwo, defaultHome, setDefaultHome, defaultTeamOneName, defaultTeamTwoName, generateRows }) => (
  <Card className="mb-4" style={styles.card}>
    <Card.Header style={styles.cardHeader}>
      <h5 className="mb-0">Fixture Settings</h5>
      <small style={{ color: '#aaa' }}>Configure default values for all fixtures</small>
    </Card.Header>
    <Card.Body className="p-3 p-md-4">
      <Row className="g-3">
        <Col xs={12} md={6} lg={3}>
          <Form.Group>
            <Form.Label className="text-light small mb-1">Date</Form.Label>
            <Form.Control
              type="date"
              value={sharedDate}
              onChange={(e) => setSharedDate(e.target.value)}
              style={styles.input}
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={6} lg={3}>
          <Form.Group>
            <Form.Label className="text-light small mb-1">School/Club 1</Form.Label>
            <SearchableSelect
              options={uniqueClubs}
              value={defaultTeamOne}
              onChange={(option) => setDefaultTeamOne(option?.clubId || option?.id || '')}
              placeholder="Search clubs..."
              loading={teamsLoading}
              id="defaultTeamOne"
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={6} lg={3}>
          <Form.Group>
            <Form.Label className="text-light small mb-1">School/Club 2</Form.Label>
            <SearchableSelect
              options={uniqueClubs}
              value={defaultTeamTwo}
              onChange={(option) => setDefaultTeamTwo(option?.clubId || option?.id || '')}
              placeholder="Search clubs..."
              loading={teamsLoading}
              id="defaultTeamTwo"
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={6} lg={3}>
          <Form.Group>
            <Form.Label className="text-light small mb-1">Home Team</Form.Label>
            <Form.Select
              value={defaultHome}
              onChange={(e) => setDefaultHome(e.target.value)}
              style={styles.select}
            >
              <option value="Neutral">Neutral</option>
              <option value="teamOne" disabled={!defaultTeamOne}>{defaultTeamOneName}</option>
              <option value="teamTwo" disabled={!defaultTeamTwo}>{defaultTeamTwoName}</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Button
            className="w-100 mt-2"
            style={styles.btnPrimary}
            onClick={generateRows}
          >
            <PlusLg className="me-2" />
            Auto Generate Rows
          </Button>
        </Col>
      </Row>
    </Card.Body>
  </Card>
));

// Mobile Fixture Card - for mobile view
const MobileFixtureCard = React.memo(({ fixture, uniqueClubs, updateFixture, duplicateFixture, deleteFixture }) => (
  <Card className="mb-3" style={{ ...styles.card, border: '1px solid rgba(0, 179, 65, 0.3)' }}>
    <Card.Body className="p-3">
      <Row className="g-2 mb-3">
        <Col xs={6}>
          <Form.Group>
            <Form.Label className="small mb-1" style={{ color: '#aaa' }}>
              <ClockFill className="me-1" size={10} />Time
            </Form.Label>
            <Form.Control
              type="time"
              size="sm"
              value={fixture.fixtureTime}
              onChange={(e) => updateFixture(fixture.id, 'fixtureTime', e.target.value)}
              style={styles.input}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Team 1 */}
      <div className="p-2 rounded mb-2" style={{ background: 'rgba(0, 179, 65, 0.1)' }}>
        <small className="text-success fw-bold d-block mb-2">TEAM 1</small>
        <Row className="g-2">
          <Col xs={8}>
            <SearchableSelect
              options={uniqueClubs}
              value={fixture.teamOneId}
              onChange={(option) => updateFixture(fixture.id, { teamOneId: option?.clubId || '', teamOneName: option?.name || option?.clubname || '' })}
              placeholder="Select club..."
              id={`team1-${fixture.id}`}
            />
          </Col>
          <Col xs={4}>
            <Form.Select
              size="sm"
              value={fixture.teamOneClassification}
              onChange={(e) => updateFixture(fixture.id, 'teamOneClassification', e.target.value)}
              style={styles.select}
            >
              <option value="">Level</option>
              {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
          </Col>
          <Col xs={12}>
            <Form.Control
              type="number"
              size="sm"
              placeholder="Score"
              value={fixture.teamOneScore}
              onChange={(e) => updateFixture(fixture.id, 'teamOneScore', e.target.value)}
              style={{ ...styles.input, textAlign: 'center', fontWeight: 'bold', fontSize: '18px' }}
              min={0}
            />
          </Col>
        </Row>
      </div>

      <div className="text-center my-2">
        <Badge bg="secondary">VS</Badge>
      </div>

      {/* Team 2 */}
      <div className="p-2 rounded mb-3" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
        <small className="text-primary fw-bold d-block mb-2">TEAM 2</small>
        <Row className="g-2">
          <Col xs={8}>
            <SearchableSelect
              options={uniqueClubs}
              value={fixture.teamTwoId}
              onChange={(option) => updateFixture(fixture.id, { teamTwoId: option?.clubId || '', teamTwoName: option?.name || option?.clubname || '' })}
              placeholder="Select club..."
              id={`team2-${fixture.id}`}
            />
          </Col>
          <Col xs={4}>
            <Form.Select
              size="sm"
              value={fixture.teamTwoClassification}
              onChange={(e) => updateFixture(fixture.id, 'teamTwoClassification', e.target.value)}
              style={styles.select}
            >
              <option value="">Level</option>
              {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
          </Col>
          <Col xs={12}>
            <Form.Control
              type="number"
              size="sm"
              placeholder="Score"
              value={fixture.teamTwoScore}
              onChange={(e) => updateFixture(fixture.id, 'teamTwoScore', e.target.value)}
              style={{ ...styles.input, textAlign: 'center', fontWeight: 'bold', fontSize: '18px' }}
              min={0}
            />
          </Col>
        </Row>
      </div>

      {/* Actions */}
      <div className="d-flex gap-2">
        <Button
          variant="outline-secondary"
          size="sm"
          className="flex-grow-1"
          onClick={() => duplicateFixture(fixture)}
        >
          <Files className="me-1" /> Duplicate
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          className="flex-grow-1"
          onClick={() => deleteFixture(fixture.id)}
        >
          <Trash className="me-1" /> Delete
        </Button>
      </div>
    </Card.Body>
  </Card>
));

// Sub-component: Fixtures Table - Desktop view
const FixturesTable = React.memo(({ fixtures, uniqueClubs, updateFixture, duplicateFixture, deleteFixture }) => (
  <>
    {/* Desktop Table */}
    <div className="d-none d-lg-block">
      <div className="table-responsive" style={{ ...styles.card, borderRadius: '12px', overflow: 'hidden' }}>
        <Table className="mb-0" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ ...styles.tableHeader, width: '70px' }}>Time</th>
              <th style={{ ...styles.tableHeader, width: '28%' }}>School/Club 1</th>
              <th style={{ ...styles.tableHeader, width: '48px' }}>Level</th>
              <th style={{ ...styles.tableHeader, width: '38px' }}>Score</th>
              <th style={{ ...styles.tableHeader, width: '24px', textAlign: 'center' }}>vs</th>
              <th style={{ ...styles.tableHeader, width: '38px' }}>Score</th>
              <th style={{ ...styles.tableHeader, width: '28%' }}>School/Club 2</th>
              <th style={{ ...styles.tableHeader, width: '48px' }}>Level</th>
              <th style={{ ...styles.tableHeader, width: '75px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fixtures.map((fixture) => (
              <tr key={fixture.id}>
                <td style={styles.tableCell}>
                  <Form.Control
                    type="time"
                    size="sm"
                    value={fixture.fixtureTime}
                    onChange={(e) => updateFixture(fixture.id, 'fixtureTime', e.target.value)}
                    style={{ ...styles.input, padding: '2px 4px', fontSize: '12px' }}
                  />
                </td>
                <td style={styles.tableCell}>
                  <SearchableSelect
                    options={uniqueClubs}
                    value={fixture.teamOneId}
                    onChange={(option) => updateFixture(fixture.id, { teamOneId: option?.clubId || '', teamOneName: option?.name || option?.clubname || '' })}
                    placeholder="Search..."
                    id={`desktop-team1-${fixture.id}`}
                  />
                </td>
                <td style={styles.tableCell}>
                  <Form.Select
                    size="sm"
                    value={fixture.teamOneClassification}
                    onChange={(e) => updateFixture(fixture.id, 'teamOneClassification', e.target.value)}
                    style={{ ...styles.select, padding: '2px 4px', fontSize: '12px' }}
                  >
                    <option value="">-</option>
                    {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Form.Select>
                </td>
                <td style={{ ...styles.tableCell, padding: '2px' }}>
                  <Form.Control
                    type="number"
                    size="sm"
                    value={fixture.teamOneScore}
                    onChange={(e) => updateFixture(fixture.id, 'teamOneScore', e.target.value)}
                    className="score-input"
                    style={{ ...styles.input, textAlign: 'center', padding: '2px 4px', fontSize: '13px', fontWeight: 'bold' }}
                    min={0}
                    max={999}
                  />
                </td>
                <td style={{ ...styles.tableCell, textAlign: 'center', padding: '0', width: '24px' }}>
                  <span style={{ color: '#666', fontSize: '11px' }}>vs</span>
                </td>
                <td style={{ ...styles.tableCell, padding: '2px' }}>
                  <Form.Control
                    type="number"
                    size="sm"
                    value={fixture.teamTwoScore}
                    onChange={(e) => updateFixture(fixture.id, 'teamTwoScore', e.target.value)}
                    className="score-input"
                    style={{ ...styles.input, textAlign: 'center', padding: '2px 4px', fontSize: '13px', fontWeight: 'bold' }}
                    min={0}
                    max={999}
                  />
                </td>
                <td style={styles.tableCell}>
                  <SearchableSelect
                    options={uniqueClubs}
                    value={fixture.teamTwoId}
                    onChange={(option) => updateFixture(fixture.id, { teamTwoId: option?.clubId || '', teamTwoName: option?.name || option?.clubname || '' })}
                    placeholder="Search..."
                    id={`desktop-team2-${fixture.id}`}
                  />
                </td>
                <td style={styles.tableCell}>
                  <Form.Select
                    size="sm"
                    value={fixture.teamTwoClassification}
                    onChange={(e) => updateFixture(fixture.id, 'teamTwoClassification', e.target.value)}
                    style={{ ...styles.select, padding: '2px 4px', fontSize: '12px' }}
                  >
                    <option value="">-</option>
                    {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Form.Select>
                </td>
                <td style={styles.tableCell}>
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => duplicateFixture(fixture)}
                      title="Duplicate"
                      style={{ padding: '4px 8px' }}
                    >
                      <Files size={14} />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => deleteFixture(fixture.id)}
                      title="Delete"
                      style={{ padding: '4px 8px' }}
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>

    {/* Mobile Card View */}
    <div className="d-lg-none">
      {fixtures.map((fixture) => (
        <MobileFixtureCard
          key={fixture.id}
          fixture={fixture}
          uniqueClubs={uniqueClubs}
          updateFixture={updateFixture}
          duplicateFixture={duplicateFixture}
          deleteFixture={deleteFixture}
        />
      ))}
    </div>
  </>
));

// Sub-component: Empty State
const EmptyState = React.memo(({ addNewRow }) => (
  <Card className="text-center py-5" style={{ ...styles.card, border: '2px dashed rgba(255, 255, 255, 0.2)' }}>
    <Card.Body>
      <PlusLg size={48} className="mb-3" style={{ color: '#666' }} />
      <h5 className="text-white mb-2">No fixtures yet</h5>
      <p className="mb-4" style={{ color: '#aaa' }}>Add fixtures using the form above or click below to add manually</p>
      <Button style={styles.btnPrimary} onClick={addNewRow}>
        <PlusLg className="me-2" />
        Add Your First Fixture
      </Button>
    </Card.Body>
  </Card>
));

// Sub-component: Upload Confirmation Modal
const UploadConfirmationModal = React.memo(({ show, onHide, fixtures, uploading, handleUpload }) => (
  <Modal show={show} onHide={onHide} centered>
    <Modal.Header closeButton style={{ background: '#1a1a1a', borderColor: '#333' }}>
      <Modal.Title className="text-white">Confirm Upload</Modal.Title>
    </Modal.Header>
    <Modal.Body style={{ background: '#1a1a1a' }}>
      <p className="text-white">
        Ready to upload <strong className="text-success">{fixtures.length} fixture{fixtures.length !== 1 ? 's' : ''}</strong>?
      </p>
      <div className="p-3 rounded" style={{ background: '#f5f5f5', maxHeight: '200px', overflowY: 'auto' }}>
        {fixtures.map((f, i) => (
          <small key={i} className="d-block py-1 text-dark">
            {f.teamOneName} ({f.teamOneClassification}) {f.teamOneScore} vs {f.teamTwoScore} {f.teamTwoName} ({f.teamTwoClassification})
          </small>
        ))}
      </div>
    </Modal.Body>
    <Modal.Footer style={{ background: '#1a1a1a', borderColor: '#333' }}>
      <Button variant="outline-secondary" onClick={onHide} disabled={uploading}>
        Cancel
      </Button>
      <Button style={styles.btnPrimary} onClick={handleUpload} disabled={uploading}>
        {uploading ? 'Uploading...' : 'Upload'}
      </Button>
    </Modal.Footer>
  </Modal>
));

// ============================================================================
// MAIN COMPONENT - CoachPortalFixtures
// ============================================================================
export default function CoachPortalFixtures() {
  const { token } = useAuth();
  const { isDevMode, apiDomain, handleLogoClick } = useDevMode();

  // State declarations
  const [fixtures, setFixtures] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [uniqueClubs, setUniqueClubs] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'info' });
  const [uploading, setUploading] = useState(false);
  const [sharedDate, setSharedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [defaultTeamOne, setDefaultTeamOne] = useState('');
  const [defaultTeamTwo, setDefaultTeamTwo] = useState('');
  const [defaultHome, setDefaultHome] = useState('Neutral');

  // Derived: clubname|classification → teamId map (used by generateRows)
  const teamMappings = useMemo(() => {
    const m = new Map();
    for (const t of allTeams) {
      m.set(`${t.clubname}|${t.teamclassification}`, t.teamid);
    }
    return m;
  }, [allTeams]);

  // Show alert helper
  const showAlert = useCallback((message, variant = 'info') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert(prev => ({ ...prev, show: false })), 5000);
  }, []);

  const findTeamById = useCallback((teamId) => {
    let team = uniqueClubs.find((t) => t.clubId === teamId || t.id === teamId);
    if (team) return team;
    team = allTeams.find((t) => t.teamid === teamId || t.id === teamId || t.clubid === teamId);
    return team || null;
  }, [uniqueClubs, allTeams]);

  const defaultTeamOneName = useMemo(() => defaultTeamOne ? findTeamById(defaultTeamOne)?.name || 'Default Club 1' : 'Select Club 1 first', [defaultTeamOne, findTeamById]);
  const defaultTeamTwoName = useMemo(() => defaultTeamTwo ? findTeamById(defaultTeamTwo)?.name || 'Default Club 2' : 'Select Club 2 first', [defaultTeamTwo, findTeamById]);

  const updateFixture = useCallback((id, fieldOrUpdates, value) => {
    setFixtures((prev) => prev.map((f) =>
      f.id === id ? { ...f, ...(typeof fieldOrUpdates === 'object' ? fieldOrUpdates : { [fieldOrUpdates]: value }) } : f
    ));
  }, []);

  // Load teams from the .NET API. No realtime subscription — admins update teams
  // infrequently and a page refresh is acceptable.
  useEffect(() => {
    const loadTeams = async () => {
      if (!apiDomain) return;
      setTeamsLoading(true);
      setTeamsError('');
      try {
        const data = await fetchTeamsFromApi(apiDomain);
        setAllTeams(data.allTeams);
        setUniqueClubs(data.uniqueClubs);
        if (data.uniqueClubs.length === 0) setTeamsError('No clubs returned by API');
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load teams';
        setTeamsError(errorMessage);
        showAlert(`⚠️ ${errorMessage}`, 'warning');
        console.error('Team load error:', error);
      } finally {
        setTeamsLoading(false);
      }
    };
    loadTeams();
  }, [apiDomain, showAlert]);

  const getTeamDetails = useCallback((clubId, classification) => {
    const team = allTeams.find((t) => (t.clubid === clubId || t.id === clubId) && t.teamclassification === classification);
    return team ? { ...team, teamid: team.teamid || team.id, clubId: team.clubid || team.id } : null;
  }, [allTeams]);

  const generateRows = useCallback(() => {
    if (!defaultTeamOne || !defaultTeamTwo) {
      showAlert('Please select default clubs first', 'warning');
      return;
    }
    if (teamMappings.size === 0) {
      showAlert('Team mappings still loading...', 'warning');
      return;
    }
    const club1 = findTeamById(defaultTeamOne);
    const club2 = findTeamById(defaultTeamTwo);
    const clubName1 = club1?.name || club1?.clubname || 'Club 1';
    const clubName2 = club2?.name || club2?.clubname || 'Club 2';

    // Find all classifications for each club
    const club1Keys = Array.from(teamMappings.keys()).filter(k => k.startsWith(clubName1 + '|'));
    const club2Keys = Array.from(teamMappings.keys()).filter(k => k.startsWith(clubName2 + '|'));

    // Extract just the classifications (e.g., "19A", "16C")
    const club1Classifications = new Set(club1Keys.map(k => k.split('|')[1]));
    const club2Classifications = new Set(club2Keys.map(k => k.split('|')[1]));

    // Find matching classifications between both clubs - only A and B teams
    const isAorBTeam = (classification) => /^[0-9]+(A|B)$/i.test(classification);
    const matchingClassifications = [...club1Classifications]
      .filter(c => club2Classifications.has(c) && isAorBTeam(c))
      .sort((a, b) => {
        // Sort by age descending (19, 18, 16...), then by letter (A, B)
        const ageA = parseInt(a.match(/\d+/)?.[0] || '0');
        const ageB = parseInt(b.match(/\d+/)?.[0] || '0');
        if (ageB !== ageA) return ageB - ageA;
        return a.localeCompare(b);
      });

    console.log('Generating rows for:', { clubName1, clubName2, mappingsSize: teamMappings.size });
    console.log('Club1 A/B classifications:', [...club1Classifications].filter(isAorBTeam));
    console.log('Club2 A/B classifications:', [...club2Classifications].filter(isAorBTeam));
    console.log('Matching A/B classifications:', matchingClassifications);

    if (matchingClassifications.length === 0) {
      showAlert(`⚠️ No matching age groups found. ${clubName1} has: ${[...club1Classifications].join(', ') || 'none'}. ${clubName2} has: ${[...club2Classifications].join(', ') || 'none'}`, 'warning');
      return;
    }

    const newFixtures = [];

    // Generate fixtures for all matching classifications
    matchingClassifications.forEach((classification) => {
      newFixtures.push({
        ...defaultFixture,
        id: Date.now() + Math.random() * 1000,
        teamOneId: defaultTeamOne,
        teamOneName: clubName1,
        teamOneClassification: classification,
        teamTwoId: defaultTeamTwo,
        teamTwoName: clubName2,
        teamTwoClassification: classification,
        homeTeamId: defaultHome === 'teamOne' ? defaultTeamOne : (defaultHome === 'teamTwo' ? defaultTeamTwo : 'Neutral'),
        homeTeamName: defaultHome === 'teamOne' ? clubName1 : (defaultHome === 'teamTwo' ? clubName2 : ''),
      });
    });

    if (newFixtures.length > 0) {
      setFixtures((prev) => [...prev, ...newFixtures]);
      showAlert(`Generated ${newFixtures.length} fixture${newFixtures.length > 1 ? 's' : ''} for matching age groups`, 'success');
    }
  }, [defaultTeamOne, defaultTeamTwo, defaultHome, teamMappings, findTeamById, showAlert]);

  const duplicateFixture = useCallback((fixture) => {
    const newFixture = { ...fixture, id: Date.now() };
    setFixtures((prev) => [...prev, newFixture]);
    showAlert('Fixture duplicated', 'success');
  }, [showAlert]);

  const deleteFixture = useCallback((id) => {
    setFixtures((prev) => prev.filter((f) => f.id !== id));
    showAlert('Fixture removed', 'info');
  }, [showAlert]);

  const addNewRow = useCallback(() => {
    setFixtures((prev) => [...prev, { ...defaultFixture, id: Date.now() }]);
  }, []);

  const generatePayload = useCallback(() => {
    const payload = fixtures
      .map((f) => {
        const team1 = getTeamDetails(f.teamOneId, f.teamOneClassification);
        const team2 = getTeamDetails(f.teamTwoId, f.teamTwoClassification);
        if (!team1 || !team2) {
          console.warn(`Skipping invalid fixture: ${f.teamOneName} vs ${f.teamTwoName}`);
          return null;
        }
        let homeTeamIdValue = 'Neutral';
        if (f.homeTeamId && f.homeTeamId !== 'Neutral') {
          homeTeamIdValue = f.homeTeamId === f.teamOneId ? team1.teamid : (f.homeTeamId === f.teamTwoId ? team2.teamid : 'Neutral');
        }
        const fixtureDateTime = localInputToApiUtc(`${sharedDate}T${f.fixtureTime || '14:00'}:00`);
        return {
          teamOneId: team1.teamid,
          teamTwoId: team2.teamid,
          teamOneScore: parseInt(f.teamOneScore) || null,
          teamTwoScore: parseInt(f.teamTwoScore) || null,
          date: fixtureDateTime,
          homeTeamId: homeTeamIdValue,
        };
      })
      .filter(Boolean);
    return payload;
  }, [fixtures, sharedDate, getTeamDetails]);

  const handleUpload = useCallback(async () => {
    if (fixtures.length === 0) {
      showAlert('Add at least one fixture', 'warning');
      return;
    }
    if (!sharedDate?.trim()) {
      showAlert('Date required', 'danger');
      return;
    }
    const allErrors = [];
    fixtures.forEach((f, i) => {
      const errors = validateFixture(f, getTeamDetails);
      if (Object.keys(errors).length > 0) allErrors.push(`Row ${i + 1}: ${Object.values(errors).join(', ')}`);
    });
    if (allErrors.length > 0) {
      showAlert(allErrors.join(' | '), 'danger');
      return;
    }
    if (!token) {
      showAlert('Not authenticated', 'danger');
      return;
    }

    setUploading(true);
    try {
      const payload = generatePayload();
      if (payload.length === 0) {
        showAlert('No valid fixtures to upload — check team mappings.', 'warning');
        return;
      }
      const apiUrl = `${apiDomain}/dataimport/fixtures/object`;
      const response = await apiClient.post(apiUrl, payload);
      if (response.status >= 200 && response.status < 300) {
        showAlert(`Successfully uploaded ${payload.length} fixture${payload.length !== 1 ? 's' : ''}`, 'success');
        setFixtures([]);
        setUploadDialogOpen(false);
      } else {
        showAlert(`Upload completed with status ${response.status}. Check logs for details.`, 'warning');
      }
    } catch (error) {
      console.error('Upload error:', error.response || error);
      const errorMsg = error.response?.data?.message || error.response?.statusText || error.message;
      showAlert(errorMsg, 'danger');
    } finally {
      setUploading(false);
    }
  }, [fixtures, sharedDate, getTeamDetails, token, apiDomain, generatePayload, showAlert]);

  if (teamsLoading) {
    return (
      <div style={styles.page} className="d-flex justify-content-center align-items-center">
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Container fluid className="px-3 px-md-4">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="position-relative d-inline-block">
            <img
              src={logoImage}
              alt="WazzaScore"
              style={{ height: 50, marginBottom: 16, cursor: 'pointer' }}
              onClick={handleLogoClick}
            />
            {isDevMode && (
              <Badge
                bg="warning"
                className="position-absolute"
                style={{ top: '50%', left: '100%', transform: 'translateY(-50%)', marginLeft: '8px' }}
              >
                DEV MODE
              </Badge>
            )}
          </div>
          <h2 className="text-white fw-bold mb-1">Coach Portal</h2>
          <p className="mb-0" style={{ color: '#aaa' }}>Upload fixtures and get instant statistic insights</p>
          {teamsError && (
            <Alert variant="warning" className="mt-2 d-inline-block">
              ⚠️ {teamsError}
            </Alert>
          )}
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <FixtureSettings
            sharedDate={sharedDate}
            setSharedDate={setSharedDate}
            uniqueClubs={uniqueClubs}
            teamsLoading={teamsLoading}
            defaultTeamOne={defaultTeamOne}
            setDefaultTeamOne={setDefaultTeamOne}
            defaultTeamTwo={defaultTeamTwo}
            setDefaultTeamTwo={setDefaultTeamTwo}
            defaultHome={defaultHome}
            setDefaultHome={setDefaultHome}
            defaultTeamOneName={defaultTeamOneName}
            defaultTeamTwoName={defaultTeamTwoName}
            generateRows={generateRows}
          />

          {/* Upload Summary Card */}
          {fixtures.length > 0 && (
            <Card className="mb-4" style={styles.card}>
              <Card.Body className="p-3">
                <Row className="align-items-center g-3">
                  <Col xs={12} md={6}>
                    <small className="d-block" style={{ color: '#aaa' }}>Fixtures Pending Upload</small>
                    <span className="text-success fw-bold fs-4">
                      {fixtures.length} fixture{fixtures.length !== 1 ? 's' : ''}
                    </span>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="d-flex gap-2 flex-wrap justify-content-md-end">
                      <Button
                        style={styles.btnPrimary}
                        onClick={() => setUploadDialogOpen(true)}
                        disabled={uploading}
                        className="flex-grow-1 flex-md-grow-0"
                      >
                        <Upload className="me-2" />Upload All
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => setFixtures([])}
                        className="flex-grow-1 flex-md-grow-0"
                        style={styles.btnOutline}
                      >
                        Clear All
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Fixtures Table/Cards */}
          {fixtures.length > 0 ? (
            <FixturesTable
              fixtures={fixtures}
              uniqueClubs={uniqueClubs}
              updateFixture={updateFixture}
              duplicateFixture={duplicateFixture}
              deleteFixture={deleteFixture}
            />
          ) : (
            <EmptyState addNewRow={addNewRow} />
          )}

          {/* Add Row Button */}
          <Button
            className="mt-3"
            style={styles.btnPrimary}
            onClick={addNewRow}
          >
            <PlusLg className="me-2" />
            Add New Row
          </Button>
        </div>

        {/* Alert Toast */}
        {alert.show && (
          <div
            className="position-fixed"
            style={{ bottom: '20px', left: '20px', right: '20px', maxWidth: '400px', zIndex: 1060 }}
          >
            <Alert
              variant={alert.variant}
              onClose={() => setAlert(prev => ({ ...prev, show: false }))}
              dismissible
            >
              {alert.message}
            </Alert>
          </div>
        )}

        {/* Modals */}
        <UploadConfirmationModal
          show={uploadDialogOpen}
          onHide={() => setUploadDialogOpen(false)}
          fixtures={fixtures}
          uploading={uploading}
          handleUpload={handleUpload}
        />
      </Container>

      {/* Custom CSS for hover effects */}
      <style>{`
        .hover-bg:hover {
          background-color: #f0f0f0 !important;
        }
        .form-control:focus, .form-select:focus {
          border-color: #00b341;
          box-shadow: 0 0 0 0.2rem rgba(0, 179, 65, 0.25);
        }
        /* Hide number input spinners to save space */
        .score-input::-webkit-outer-spin-button,
        .score-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .score-input {
          -moz-appearance: textfield;
          appearance: textfield;
        }
        @media (max-width: 991px) {
          .table-responsive {
            border-radius: 12px;
          }
        }
      `}</style>
    </div>
  );
}
